<template>
  <div class="panel-statistics">
    <!-- Toolbar -->
    <div class="panel-toolbar">
      <p class="panel-toolbar-meta">
        <i class="fas fa-chart-line me-2 text-primary"></i>
        Métricas de ventas y clientes
        <span v-if="periodLabel" class="period-badge">{{ periodLabel }}</span>
      </p>
      <div class="panel-toolbar-actions">
        <button
          class="btn btn-sm btn-outline-light"
          @click="loadData"
          :disabled="loading"
          title="Actualizar"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
    </div>

    <!-- Filtros de periodo -->
    <div class="filters-bar panel-card">
      <div class="filters-row">
        <label class="filter-label">Periodo</label>
        <select
          v-model="period"
          class="form-select form-select-sm period-select"
          @change="onPeriodChange"
          :disabled="loading"
        >
          <option value="7">Últimos 7 días</option>
          <option value="30">Últimos 30 días</option>
          <option value="90">Últimos 90 días</option>
          <option value="365">Último año</option>
          <option value="all">Todo el tiempo</option>
          <option value="custom">Rango personalizado</option>
        </select>
      </div>

      <template v-if="period === 'custom'">
        <div class="filters-row">
          <label class="filter-label" for="stats-start">Desde</label>
          <input
            id="stats-start"
            v-model="startDate"
            type="date"
            class="form-control form-control-sm date-input"
            :max="endDate || todayIso"
            :disabled="loading"
          />
        </div>
        <div class="filters-row">
          <label class="filter-label" for="stats-end">Hasta</label>
          <input
            id="stats-end"
            v-model="endDate"
            type="date"
            class="form-control form-control-sm date-input"
            :min="startDate"
            :max="todayIso"
            :disabled="loading"
          />
        </div>
        <button
          class="btn btn-sm btn-primary apply-btn"
          @click="loadData"
          :disabled="loading || !canApplyCustom"
        >
          <i class="fas fa-filter me-1"></i>Aplicar
        </button>
      </template>

      <button
        v-if="period !== 'custom'"
        type="button"
        class="btn btn-sm btn-link clear-link"
        @click="resetFilters"
        :disabled="loading"
      >
        Restablecer
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading && !data" class="stats-loading">
      <div class="spinner-border text-primary"></div>
      <p>Cargando estadísticas…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="state-box state-error">
      <i class="fas fa-exclamation-circle fa-2x mb-3"></i>
      <p>{{ error }}</p>
      <button class="btn btn-primary btn-sm mt-2" @click="loadData">Reintentar</button>
    </div>

    <template v-else-if="data">
      <!-- KPIs principales -->
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-icon"><i class="fas fa-dollar-sign"></i></div>
          <div class="kpi-body">
            <span class="kpi-label">Facturación</span>
            <span class="kpi-value">{{ formatCurrency(data.summary.total_revenue) }}</span>
            <span v-if="data.summary.revenue_change" class="kpi-change" :class="changeClass(data.summary.revenue_change)">
              <i :class="changeIcon(data.summary.revenue_change)"></i>
              {{ Math.abs(data.summary.revenue_change) }}% vs periodo anterior
            </span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon"><i class="fas fa-receipt"></i></div>
          <div class="kpi-body">
            <span class="kpi-label">Ticket promedio</span>
            <span class="kpi-value">{{ formatCurrency(data.summary.avg_ticket) }}</span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon"><i class="fas fa-shopping-bag"></i></div>
          <div class="kpi-body">
            <span class="kpi-label">Pedidos facturables</span>
            <span class="kpi-value">{{ data.summary.billable_orders ?? data.summary.completed_orders }}</span>
            <span v-if="data.summary.orders_change" class="kpi-change" :class="changeClass(data.summary.orders_change)">
              <i :class="changeIcon(data.summary.orders_change)"></i>
              {{ Math.abs(data.summary.orders_change) }}% vs periodo anterior
            </span>
          </div>
        </div>

        <div class="kpi-card" :class="{ 'kpi-alert': data.summary.pending_orders > 0 }">
          <div class="kpi-icon"><i class="fas fa-clock"></i></div>
          <div class="kpi-body">
            <span class="kpi-label">Pendientes de atención</span>
            <span class="kpi-value">{{ data.summary.pending_orders }}</span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon"><i class="fas fa-users"></i></div>
          <div class="kpi-body">
            <span class="kpi-label">Clientes registrados</span>
            <span class="kpi-value">{{ data.customers.total }}</span>
            <span class="kpi-sub">+{{ data.customers.new_in_period }} en el periodo</span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon"><i class="fas fa-user-check"></i></div>
          <div class="kpi-body">
            <span class="kpi-label">Clientes con compras</span>
            <span class="kpi-value">{{ data.customers.active_in_period }}</span>
            <span class="kpi-sub">En el periodo seleccionado</span>
          </div>
        </div>
      </div>

      <!-- Gráfico -->
      <div class="stats-section panel-card chart-section">
        <div class="section-header">
          <h3><i class="fas fa-chart-area me-2"></i>Compras por periodo</h3>
          <div class="chart-legend">
            <span class="legend-item"><span class="dot dot-revenue"></span> Facturación</span>
            <span class="legend-item"><span class="dot dot-orders"></span> Pedidos</span>
          </div>
        </div>
        <div v-if="!timeline.length" class="chart-empty">
          <i class="fas fa-chart-bar fa-2x mb-2"></i>
          <p>Sin datos de ventas en este periodo</p>
        </div>
        <div v-else class="chart-wrap">
          <canvas ref="chartCanvas"></canvas>
        </div>
      </div>

      <!-- Tablas -->
      <div class="stats-row">
        <div class="stats-section panel-table-wrap">
          <div class="section-header px-3 pt-3">
            <h3><i class="fas fa-seedling me-2"></i>Productos más vendidos</h3>
          </div>
          <div v-if="!data.top_products.length" class="table-empty">Sin datos en el periodo</div>
          <div v-else class="table-responsive">
            <table class="table table-dark table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Producto</th>
                  <th class="text-end">Unidades</th>
                  <th class="text-end">Facturación</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in data.top_products" :key="p.product__id">
                  <td class="rank">{{ i + 1 }}</td>
                  <td>{{ p.product__name }}</td>
                  <td class="text-end">{{ p.units_sold }}</td>
                  <td class="text-end fw-semibold">{{ formatCurrencyShort(p.revenue) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="stats-section panel-table-wrap">
          <div class="section-header px-3 pt-3">
            <h3><i class="fas fa-trophy me-2"></i>Clientes con más compras</h3>
          </div>
          <div v-if="!data.top_customers_by_spend.length" class="table-empty">Sin datos en el periodo</div>
          <div v-else class="table-responsive">
            <table class="table table-dark table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cliente</th>
                  <th class="text-end">Pedidos</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(c, i) in data.top_customers_by_spend"
                  :key="c.user__id"
                  class="clickable-row"
                  @click="goToUser(c.user__id)"
                >
                  <td class="rank">{{ i + 1 }}</td>
                  <td>
                    <span class="customer-name">{{ customerName(c) }}</span>
                    <small class="d-block text-muted-custom">{{ c.user__email }}</small>
                  </td>
                  <td class="text-end">{{ c.order_count }}</td>
                  <td class="text-end fw-semibold">{{ formatCurrencyShort(c.total_spent) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Clientes frecuentes + stock -->
      <div class="stats-row">
        <div class="stats-section panel-table-wrap">
          <div class="section-header px-3 pt-3">
            <h3><i class="fas fa-redo me-2"></i>Clientes más frecuentes</h3>
          </div>
          <div v-if="!data.top_customers_by_frequency.length" class="table-empty">Sin datos en el periodo</div>
          <div v-else class="table-responsive">
            <table class="table table-dark table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cliente</th>
                  <th class="text-end">Pedidos</th>
                  <th class="text-end">Gastó</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(c, i) in data.top_customers_by_frequency"
                  :key="'freq-' + c.user__id"
                  class="clickable-row"
                  @click="goToUser(c.user__id)"
                >
                  <td class="rank">{{ i + 1 }}</td>
                  <td>{{ c.user__username }}</td>
                  <td class="text-end fw-semibold">{{ c.order_count }}</td>
                  <td class="text-end">{{ formatCurrencyShort(c.total_spent) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="stats-section panel-card stock-section">
          <div class="section-header">
            <h3><i class="fas fa-box me-2"></i>Alertas de stock</h3>
            <router-link to="/panel/productos" class="btn btn-sm btn-outline-light">Ver productos</router-link>
          </div>
          <div v-if="!hasStockAlerts" class="table-empty success">
            <i class="fas fa-check-circle me-2"></i> Sin alertas de stock
          </div>
          <ul v-else class="stock-list">
            <li v-for="p in data.stock.out_of_stock.slice(0, 4)" :key="'out-' + p.id" class="stock-item stock-out">
              <span>{{ p.product__name }} <small>({{ p.seed_count }} sem.)</small></span>
              <span class="badge bg-danger">Sin stock</span>
            </li>
            <li v-for="p in data.stock.low_stock.slice(0, 4)" :key="'low-' + p.id" class="stock-item">
              <span>{{ p.product__name }} <small>({{ p.seed_count }} sem.)</small></span>
              <span class="badge bg-warning text-dark">{{ p.stock }} ud.</span>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import {
  Chart,
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { fetchOrderAnalytics } from '@/services/analytics'

Chart.register(
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
)

const router = useRouter()
const data = ref(null)
const loading = ref(false)
const error = ref(null)
const period = ref('30')
const startDate = ref('')
const endDate = ref('')
const chartCanvas = ref(null)
let chartInstance = null

const todayIso = new Date().toISOString().split('T')[0]

const canApplyCustom = computed(() => {
  if (!startDate.value || !endDate.value) return false
  return startDate.value <= endDate.value
})

const periodLabel = computed(() => {
  if (!data.value?.period) return ''
  const p = data.value.period
  if (p.start_date && p.end_date) {
    return `${formatDisplayDate(p.start_date)} — ${formatDisplayDate(p.end_date)}`
  }
  return p.label || ''
})

const formatDisplayDate = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const onPeriodChange = () => {
  if (period.value === 'custom') {
    if (!startDate.value || !endDate.value) {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      endDate.value = end.toISOString().split('T')[0]
      startDate.value = start.toISOString().split('T')[0]
    }
    return
  }
  loadData()
}

const resetFilters = () => {
  period.value = '30'
  startDate.value = ''
  endDate.value = ''
  loadData()
}

const timeline = computed(() => data.value?.timeline ?? [])

const hasStockAlerts = computed(() => {
  if (!data.value?.stock) return false
  return data.value.stock.out_of_stock.length > 0 || data.value.stock.low_stock.length > 0
})

const formatCurrency = (v) => {
  if (v == null) return '—'
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(v)
}

const formatCurrencyShort = (v) => {
  if (v == null) return '—'
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`
  return `$${Math.round(v)}`
}

const customerName = (c) => {
  const full = `${c.user__first_name || ''} ${c.user__last_name || ''}`.trim()
  return full || c.user__username
}

const changeClass = (v) => (v >= 0 ? 'change-up' : 'change-down')
const changeIcon = (v) => (v >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down')

const goToUser = (id) => router.push(`/panel/usuarios/${id}`)

const formatChartLabel = (dateStr) => {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length === 3) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
  }
  if (parts.length >= 2) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1)
    return d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' })
  }
  return dateStr
}

const destroyChart = () => {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
}

const buildChart = () => {
  destroyChart()
  if (!chartCanvas.value || !timeline.value.length) return

  const labels = timeline.value.map((t) => formatChartLabel(t.date))
  const revenues = timeline.value.map((t) => t.revenue)
  const orders = timeline.value.map((t) => t.orders)

  chartInstance = new Chart(chartCanvas.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Facturación',
          data: revenues,
          borderColor: '#ffec00',
          backgroundColor: 'rgba(255, 236, 0, 0.08)',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#ffec00',
          tension: 0.35,
          fill: true,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Pedidos',
          data: orders,
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y1',
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1a1a1a',
          borderColor: 'rgba(255,255,255,0.1)',
          borderWidth: 1,
          titleColor: '#fff',
          bodyColor: 'rgba(255,255,255,0.85)',
          padding: 12,
          callbacks: {
            label(ctx) {
              if (ctx.dataset.label === 'Facturación') {
                return ` ${formatCurrency(ctx.raw)}`
              }
              return ` ${ctx.raw} pedidos`
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: 'rgba(255,255,255,0.04)' },
          ticks: { color: 'rgba(255,255,255,0.5)', maxRotation: 45, font: { size: 11 } },
        },
        y: {
          position: 'left',
          grid: { color: 'rgba(255,255,255,0.06)' },
          ticks: {
            color: 'rgba(255,255,255,0.5)',
            callback: (v) => formatCurrencyShort(v),
            font: { size: 11 },
          },
        },
        y1: {
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: { color: 'rgba(255,255,255,0.4)', stepSize: 1, font: { size: 11 } },
        },
      },
    },
  })
}

const loadData = async () => {
  if (period.value === 'custom') {
    if (!canApplyCustom.value) {
      error.value = 'Seleccioná un rango de fechas válido (desde ≤ hasta)'
      return
    }
  }

  loading.value = true
  error.value = null
  try {
    const params = {}
    if (period.value === 'custom') {
      params.start_date = startDate.value
      params.end_date = endDate.value
    } else {
      params.period = period.value
    }
    data.value = await fetchOrderAnalytics(params)
    await nextTick()
    buildChart()
  } catch (e) {
    error.value = e.response?.data?.error || e.response?.data?.detail || 'No se pudieron cargar las estadísticas'
    destroyChart()
  } finally {
    loading.value = false
  }
}

watch(timeline, async () => {
  await nextTick()
  buildChart()
})

onMounted(loadData)
onBeforeUnmount(destroyChart)
</script>

<style scoped>
.panel-statistics {
  color: var(--white-color);
}

.period-badge {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  background: rgba(255, 236, 0, 0.12);
  color: var(--primary-color);
  font-weight: 600;
}

.period-select {
  min-width: 170px;
  max-width: 220px;
  background: rgba(0, 0, 0, 0.25);
  border-color: var(--panel-border);
  color: var(--white-color);
  font-size: 0.85rem;
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  padding: 1rem 1.15rem;
  margin-bottom: 1.25rem;
}

.filters-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.filter-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--panel-text-muted);
  margin: 0;
}

.date-input {
  width: 160px;
  background: rgba(0, 0, 0, 0.25);
  border-color: var(--panel-border);
  color: var(--white-color);
  color-scheme: dark;
}

.date-input:focus {
  background: rgba(0, 0, 0, 0.3);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.12);
}

.apply-btn {
  align-self: flex-end;
  white-space: nowrap;
}

.clear-link {
  align-self: flex-end;
  color: var(--panel-text-muted);
  text-decoration: none;
  font-size: 0.82rem;
  padding: 0.35rem 0.5rem;
}

.clear-link:hover {
  color: var(--white-color);
}

.stats-loading {
  text-align: center;
  padding: 4rem 1rem;
  color: var(--panel-text-muted);
}

.stats-loading p {
  margin-top: 1rem;
  font-size: 0.9rem;
}

/* KPI grid — ancho completo, 3 columnas alineadas al resto del contenido */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;
  margin-bottom: 1.25rem;
  width: 100%;
}

@media (max-width: 991.98px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 575.98px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .date-input {
    width: 100%;
  }

  .period-select {
    max-width: none;
    width: 100%;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .apply-btn,
  .clear-link {
    align-self: stretch;
    width: 100%;
  }
}

.kpi-card {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 14px;
  transition: border-color 0.15s;
}

.kpi-card:hover {
  border-color: rgba(255, 255, 255, 0.14);
}

.kpi-card.kpi-alert {
  border-color: rgba(229, 72, 77, 0.45);
}

.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 236, 0, 0.12);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.95rem;
}

.kpi-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.kpi-label {
  font-size: 0.72rem;
  color: var(--panel-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 0.15rem;
}

.kpi-sub {
  font-size: 0.72rem;
  color: var(--panel-text-muted);
  margin-top: 0.2rem;
}

.kpi-change {
  font-size: 0.68rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.change-up { color: #6ee7a0; }
.change-down { color: #f87171; }

/* Sections */
.stats-section {
  margin-bottom: 1.25rem;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 0.25rem;
}

@media (max-width: 991.98px) {
  .stats-row {
    grid-template-columns: 1fr;
  }
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.section-header h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  color: var(--white-color);
}

.chart-section {
  padding: 1.25rem;
}

.chart-wrap {
  height: 280px;
  position: relative;
}

.chart-empty,
.table-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--panel-text-muted);
  font-size: 0.9rem;
}

.table-empty.success {
  color: #6ee7a0;
}

.chart-legend {
  display: flex;
  gap: 1rem;
  font-size: 0.78rem;
  color: var(--panel-text-muted);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot-revenue { background: var(--primary-color); }
.dot-orders { background: rgba(255, 255, 255, 0.35); }

.rank {
  color: var(--panel-text-muted);
  width: 2rem;
}

.text-muted-custom {
  color: var(--panel-text-muted) !important;
  font-size: 0.78rem;
}

.customer-name {
  font-weight: 500;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background: var(--panel-surface-hover) !important;
}

/* Stock list */
.stock-section {
  padding: 1.25rem;
}

.stock-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.stock-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.55rem 0;
  border-bottom: 1px solid var(--panel-border-muted);
  font-size: 0.85rem;
}

.stock-item:last-child {
  border-bottom: none;
}

.stock-item small {
  color: var(--panel-text-muted);
}

.stock-out {
  background: rgba(229, 72, 77, 0.06);
  margin: 0 -0.5rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  border-radius: 6px;
}

.state-box {
  text-align: center;
  padding: 3rem;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 14px;
  color: var(--panel-text-muted);
}

.state-error {
  border-color: rgba(229, 72, 77, 0.3);
}
</style>
